import { useMemo } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, IconButton } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { Edit } from '@mui/icons-material';
import { unique } from '../../utils/collections';
import EquipmentLoader from '../loaders/EquipmentLoader';
import { getBuildingsEntities } from '../../redux/selectors/buildings';
import { TooltipHeader, percentileColor } from './util';
import { defaultTableOptions, defaultTableInitialState } from '../util/DataTable';

const EquipmentTable = ({
  equipment,
  buildingsById,
  orgsById,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  equipmentSelection,
  onRowSelectionChange,
  equipTypesById,
}) => {
  const navigate = useNavigate();

  const data = useMemo(
    () =>
      equipment
        .values()
        .map((e) => ({
          building: buildingsById[e.buildingId],
          org: orgsById[buildingsById[e.buildingId].orgId],
          ...e,
        }))
        .toArray(),
    [orgsById, equipment, buildingsById]
  );

  const observedEquipTypeIds = useMemo(
    () => unique(data.map(({ equipmentTypeId }) => equipmentTypeId)),
    [data]
  );

  const columns = useMemo(() => {
    const lastHour = new Date() - 1 * 60 * 60 * 1000;
    const orgsOptions = Object.values(orgsById).map((o) => ({ label: o.name, value: o.id }));
    const equipTypeOptions = Object.entries(equipTypesById)
      .map(([value, et]) => ({
        label: et.nameAbbr,
        value: Number.parseInt(value, 10),
      }))
      .filter(({ value }) => observedEquipTypeIds.includes(value));
    const liveHeader = ({ column }) => (
      <TooltipHeader
        tooltip="Live points have been updated in the past hour."
        content={column.columnDef.header}
      />
    );
    const liveHeaderPercent = ({ column }) => (
      <TooltipHeader tooltip="Values are in percentiles" content={column.columnDef.header} />
    );
    const equipType = ({ row, renderedCellValue }) => (
      <>
        {equipTypesById[renderedCellValue]?.nameAbbr ?? renderedCellValue}
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/staging?view=Equipment&bid=${row.original.buildingId}`);
          }}
        >
          <Edit />
        </IconButton>
      </>
    );
    const equipName = ({ row, renderedCellValue }) => (
      <>
        {renderedCellValue}
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/staging?view=Overview&bid=${row.original.buildingId}`);
          }}
        >
          <Edit />
        </IconButton>
      </>
    );
    return [
      // TODO: group shared column defs in shared file
      {
        id: 'o_name',
        accessorFn: (e) => [e.org.id],
        header: 'Organization Name',
        filterVariant: 'multi-select',
        filterSelectOptions: orgsOptions,
        Cell: ({ renderedCellValue }) => orgsById[renderedCellValue]?.name ?? renderedCellValue,
      },
      {
        id: 'b_name',
        header: 'Building Name',
        accessorFn: ({ building }) => building?.name,
        filterVariant: 'autocomplete',
      },
      {
        id: 'e_name',
        accessorKey: 'name',
        header: 'Equipment Name',
        filterVariant: 'autocomplete',
        Cell: equipName,
      },
      {
        id: 'e_type',
        accessorFn: (e) => [e.equipmentTypeId],
        header: 'Equipment Type',
        filterVariant: 'multi-select',
        filterSelectOptions: equipTypeOptions,
        Cell: equipType,
      },
      {
        id: 'e_livePointCount',
        header: 'Live Points (count)',
        Header: liveHeader,
        filterVariant: 'range',
        accessorFn: ({ points }) => points?.filter((p) => p.lastUpdatedTs >= lastHour).length ?? 0,
      },
      {
        id: 'e_pointCount',
        header: 'Total Points (count)',
        filterVariant: 'range',
        accessorFn: ({ points }) => points?.length ?? 0,
      },
      {
        id: 'e_livePointPercent',
        filterVariant: 'range',
        header: 'Live Points (percent)',
        Header: liveHeaderPercent,
        // Hacking in -1 for NaNs to get sorting the way we want
        accessorFn: ({ points }) => {
          const count = points?.length ?? 0;
          const liveCount = points?.filter((p) => p.lastUpdatedTs >= lastHour).length ?? 0;
          const result = liveCount / count;
          return Number.isNaN(result) ? -1 : result * 100;
        },
        // eslint-disable-next-line react/no-unstable-nested-components
        Cell: ({ renderedCellValue }) => (
          // Taken from https://mui.com/material-ui/react-progress/ "Circular with label"
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress
              variant="determinate"
              color={percentileColor(renderedCellValue)}
              value={100}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                {renderedCellValue < 0 ? '-' : `${Math.round(renderedCellValue)}%`}
              </Typography>
            </Box>
          </Box>
        ),
      },
      {
        id: 'e_lastIngest',
        header: 'Last Data Ingested',
        sortingFn: 'datetime',
        enableColumnFilter: false,
        // hijacking the min time to seperate no ingests case from no promoted points case
        accessorFn: ({ points }) =>
          new Date(Math.max(0, ...(points?.map((p) => p?.lastUpdatedTs) ?? []))),

        Cell: ({ renderedCellValue }) => {
          if (renderedCellValue.valueOf() === 0) {
            return 'Never';
          }
          return renderedCellValue.toLocaleString();
        },
      },
      { id: 'e_feeds', accessorKey: 'feeds_equipment', header: 'FEEDS' },
      { id: 'e_fed', accessorKey: 'fed_by_equipment', header: 'FED BY' },
      { id: 'e_contained', accessorKey: 'contained_by_location', header: 'CONTAINED BY' },
      {
        id: 'e_device_ids',
        header: 'Device Ids',
        accessorFn: ({ points }) =>
          unique(points.map((p) => p.device?.device_id))
            .toSorted()
            .join(', '),
      },
      {
        id: 'e_device_names',
        header: 'Device Names',
        accessorFn: ({ points }) =>
          unique(points.map((p) => p.device?.name))
            .toSorted()
            .join(', '),
      },
      {
        id: 'e_tags',
        header: 'Haystack Tags',
        accessorFn: ({ tags }) => tags.toSorted().join(', '),
      },
      {
        id: 'e_labels',
        header: 'Custom Labels',
        accessorFn: ({ labels }) => labels.toSorted().join(', '),
      },
    ];
  }, [equipTypesById, orgsById, navigate, observedEquipTypeIds]);

  const showBuildings = Object.keys(buildingsById).length > 1;
  const showOrgs =
    showBuildings && unique(Object.values(buildingsById).map((b) => b.orgId)).length <= 1;

  const columnIds = useMemo(() => columns.map(({ id }) => id), [columns]);
  const equipSorting = useMemo(
    () => sorting.filter(({ id }) => columnIds.includes(id)),
    [sorting, columnIds]
  );
  const equipColumnFilters = useMemo(
    () => columnFilters.filter(({ id }) => columnIds.includes(id)),
    [columnFilters, columnIds]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions(true),
    columns,
    data,
    initialState: {
      ...defaultTableInitialState(equipColumnFilters, equipSorting),
      columnVisibility: {
        o_name: showOrgs,
        b_name: showBuildings,
        e_feeds: false,
        e_fed: false,
        e_contained: false,
        e_device_ids: false,
        e_device_names: false,
        e_tags: false,
        e_labels: false,
      },
    },
    onRowSelectionChange,
    // sorting
    onSortingChange,
    // filtering
    onColumnFiltersChange,
    state: {
      sorting: equipSorting,
      columnFilters: equipColumnFilters,
      rowSelection: equipmentSelection,
    },
  });

  if (Object.keys(buildingsById).length <= 0) return 'Too few buildings';
  if (Object.keys(buildingsById).length >= 10) return 'Too many buildings';
  return (
    <div>
      {Object.keys(buildingsById).map((bid) => (
        <EquipmentLoader key={bid} buildingId={bid} getDevices />
      ))}
      <MaterialReactTable table={mrt} />
    </div>
  );
};

const mapStateToProps = (state, { buildingsById, orgsById }) => ({
  ...getBuildingsEntities(state, Object.keys(buildingsById)),
  buildingsById,
  orgsById,
  equipTypesById: state.entities.equipTypes.byId || {},
});

// eslint-disable-next-line no-unused-vars
const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(EquipmentTable);
